import i18n from '@/app/i18n';
import { apiErrorCode } from '@/lib/apiErrorCode';

// Человеческие тексты ошибок для всех форм/запросов.
// Бэкенд иногда отдаёт «программистские» сообщения (gin-валидация вида
// "Field validation for 'Password' failed on the 'min' tag", "min length",
// "password does not meet complexity requirements"). Этот хелпер превращает их
// (и коды статусов) в понятный пользователю русский текст.

type MaybeApiError = {
  status?: number;
  message?: string;
  raw?: unknown;
};

const FIELD_KEYS: Record<string, string> = {
  password: 'errors.fieldPassword',
  new_password: 'errors.fieldNewPassword',
  old_password: 'errors.fieldOldPassword',
  email: 'errors.fieldEmail',
  full_name: 'errors.fieldFullName',
  name: 'errors.fieldName',
  title: 'errors.fieldName',
  slug: 'errors.fieldSlug',
  phone: 'errors.fieldPhone',
  price: 'errors.fieldPrice',
  topic: 'errors.fieldTopic',
  message: 'errors.fieldMessage',
  curator_id: 'errors.fieldCurator',
  marathon_id: 'errors.fieldMarathon',
  tariff_plan_id: 'errors.fieldTariff',
};

/**
 * Язык ответа `humanizeError` в текущем вызове; `undefined` — язык интерфейса.
 * ⚠️ Нужен моку: он целиком по-английски при любом интерфейсе (`lib/mockEnglish.ts`), а
 * тексты здесь берутся из общего экземпляра i18next, мимо английской обёртки экрана.
 */
let forcedLng: string | undefined;

function tr(key: string, opts?: Record<string, unknown>): string {
  return forcedLng ? i18n.t(key, { ...opts, lng: forcedLng }) : i18n.t(key, opts);
}

function answerLang(): string {
  return forcedLng ?? i18n.language ?? 'ru';
}

function fieldLabel(field: string): string {
  const key = FIELD_KEYS[field.toLowerCase()];
  // Незнакомое поле показываем как пришло: лучше техническое имя, чем пустота.
  return key ? tr(key) : field;
}

// gin/validator tag → человеческое сообщение
function tagMessage(field: string, tag: string, param?: string): string {
  const f = fieldLabel(field);
  const isPassword = /pass/i.test(field);
  switch (tag.toLowerCase()) {
    case 'required':
      return tr('errors.required', { field: f });
    case 'email':
      return tr('errors.email');
    case 'min':
      if (isPassword) return tr('errors.passwordShort', { n: param || 8 });
      // Без параметра длину называть нечем — тогда фраза короче.
      return param ? tr('errors.tooShort', { field: f, n: param }) : tr('errors.tooShortNoParam', { field: f });
    case 'max':
      return param ? tr('errors.tooLong', { field: f, n: param }) : tr('errors.tooLongNoParam', { field: f });
    case 'gte':
    case 'min_value':
      return tr('errors.tooSmall', { field: f, n: param || 0 });
    case 'uuid':
      return tr('errors.invalidValue', { field: f });
    default:
      return tr('errors.checkField', { field: f });
  }
}

/**
 * ⚠️ ОБРЫВ СВЯЗИ И ТАЙМАУТ — ОДНО СООБЩЕНИЕ (25.09.2026).
 *
 * Ответа сервера нет вовсе: статус 0 или его нет. До этой проверки такой отказ не попадал ни в одну ветку
 * ниже и уходил в запасной путь `return raw` — ученик видел «Network Error», «timeout of 30000ms exceeded»
 * и на сайте, и в приложении. Узнаём по коду axios (`ApiError.code` из `api/client.ts`), а для `fetch`
 * и вызовов мимо клиента — по тексту: у Chrome «Failed to fetch», у Safari «Load failed».
 */
const NETWORK_CODES: ReadonlySet<string> = new Set([
  'ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNREFUSED', 'ECONNRESET', 'ENOTFOUND',
]);
const NETWORK_TEXT =
  /^(network error|failed to fetch|load failed|request aborted|network request failed)$|timeout of \d+ms exceeded|networkerror when attempting to fetch|internet connection appears to be offline/i;

export function isNetworkError(err: unknown): boolean {
  if (!err) return false;
  const e = (typeof err === 'object' ? err : {}) as { status?: unknown; code?: unknown; message?: unknown };
  if (typeof e.status === 'number' && e.status > 0) return false;
  if (typeof e.code === 'string' && NETWORK_CODES.has(e.code)) return true;
  const msg = typeof err === 'string' ? err : typeof e.message === 'string' ? e.message : '';
  return NETWORK_TEXT.test(msg.trim());
}

/**
 * «Похоже на внутренний текст» — такой ученику не показываем никогда (25.09.2026).
 *
 * Сырой текст сервера здесь проходит на экран в нескольких местах намеренно: причина отказа 400/403,
 * сообщение лимита 423/429 — сервер знает, что именно не так. Но тем же путём шли и библиотечные фразы
 * («Request failed with status code 400», когда тела нет), и внутренние коды вида `subtype_join_outside_mirror`,
 * и ошибки базы. Одна проверка на все такие места; второй регэксп — с учётом регистра, иначе `E[A-Z]{4,}`
 * ловил бы любое слово на «e».
 */
const TECHNICAL =
  /network ?error|timeout of|\d+\s?ms\b|failed to fetch|load failed|request (failed|aborted)|status code|\b[a-z]+_[a-z0-9_]+\b|\bpq:|\bsql\b|dial tcp|context (deadline|canceled)|\bundefined\b|\bnull\b|\bnil\b|panic|exception|unexpected|goroutine|internal server|not authenticated|https?:\/\//i;
const TECHNICAL_CODE = /\b(ERR_[A-Z_]+|E[A-Z]{4,})\b/;
const HAS_CYRILLIC = /[А-Яа-яЁёӘәҒғҚқҢңӨөҰұҮүҺһІі]/;

export function looksTechnical(text: string): boolean {
  return TECHNICAL.test(text) || TECHNICAL_CODE.test(text);
}

/**
 * ⚠️ СЕРВЕРНЫЙ ТЕКСТ ПРИЧИНЫ — ТОЛЬКО НА ЯЗЫКЕ УЧЕНИКА (промпт 12, 18.10.2026).
 *
 * Причину отказа 400/403/409/423/429 сервер знает лучше нас, поэтому короткий человеческий текст показывали как есть.
 * Но сервер пишет по-русски, а часть служб — нейтральными английскими фразами: ученик видел «rate limit exceeded»,
 * «too many messages, slow down», «forbidden», «daily limit reached (30 minutes)», а казахский и английский
 * интерфейс — русские фразы о лимитах. Теперь как есть — только кириллица и только при русском интерфейсе, иначе
 * свой ключ. Как только сервер пришлёт код (шаг 2б), ветка кода выше даст текст на любом языке — фронт работает и со
 * старым ответом, и с новым.
 */
function serverReason(raw: string): string | null {
  if (!raw || raw.length >= 160 || looksTechnical(raw) || !HAS_CYRILLIC.test(raw)) return null;
  return (answerLang() || 'ru').startsWith('ru') ? raw : null;
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Поле тела отказа — из формы клиента API (`raw`) или сырой axios. */
function bodyField(err: unknown, name: string): unknown {
  if (!isObject(err)) return undefined;
  const body = isObject(err.raw) ? err.raw : isObject(err.response) && isObject(err.response.data) ? err.response.data : undefined;
  return body?.[name];
}

function positive(v: unknown): number | undefined {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
}

/** «Слишком часто»: с секундами, если сервер их сказал (`retry_after`). */
function tooFast(err: unknown): string {
  const sec = positive(bodyField(err, 'retry_after'));
  return sec ? tr('errors.tooFast', { seconds: sec }) : tr('errors.tooFastNoTime');
}

/** Недельный лимит: с числами и датой — когда сервер их пришлёт кодом `weekly_limit` (шаг 2б). */
function weeklyLimit(err: unknown): string {
  const used = positive(bodyField(err, 'used'));
  const limit = positive(bodyField(err, 'limit'));
  const next = bodyField(err, 'next');
  return used && limit && typeof next === 'string' && next
    ? tr('errors.limitWeekCount', { used, limit, next })
    : tr('errors.limitWeek');
}

/**
 * Человеческий текст ошибки. `lng` — ответить на этом языке, а не на языке интерфейса
 * (мок: `humanizeError(e, MOCK_LANGUAGE)`).
 */
export function humanizeError(err: unknown, lng?: string): string {
  const prev = forcedLng;
  forcedLng = lng;
  try {
    return humanizeErrorText(err);
  } finally {
    forcedLng = prev;
  }
}

function humanizeErrorText(err: unknown): string {
  const e = (err || {}) as MaybeApiError;
  const status = e.status;
  const raw = (e.message ?? (typeof err === 'string' ? err : '')).toString().trim();
  const low = raw.toLowerCase();

  /**
   * 0) МАШИННЫЙ КОД ОТКАЗА — РАНЬШЕ ЛЮБОГО РАЗБОРА ТЕКСТА (19.09.2026, договор
   * `selling-words-guard.md` §8.3).
   *
   * ⚠️ ТЕКСТ СООБЩЕНИЯ БОЛЬШЕ НЕ ОПОРА. Ради правила Apple 3.1.1 службы перешли на нейтральные
   * английские фразы: блокировка входа стала «too many failed attempts, try again later» вместо
   * «account is temporarily locked…», отказ по возможности — «feature not available». Разбор по
   * подстроке `temporarily locked` на новом сервере промолчал бы, и ученик после пяти неудач увидел
   * бы английский текст — ровно то, на что жаловались 07.09.2026.
   *
   * ⚠️ И ОТКАЗ ПО ВОЗМОЖНОСТИ БЕЗ ЭТОГО ПОКАЗАЛСЯ БЫ ПО-АНГЛИЙСКИ: ветка 403 ниже показывает
   * короткое сообщение сервера как есть, а оно теперь «feature not available».
   *
   * Разбор текста оставлен ниже запасным путём — для сервера, ещё не выкаченного на прод.
   */
  const code = apiErrorCode(err);
  if (code === 'too_many_attempts') return tr('errors.accountLocked');
  if (code === 'feature_unavailable') return tr('errors.featureUnavailable');
  // Чат без права (договор окна B, chat-service, 25.09.2026): 403 с текстом «chat is not available».
  // ⚠️ Без «доступ», «функция», «тариф» (правило владельца 25.09.2026) — держит `networkError.test.ts`.
  // Общий сторож продающих слов «недоступен» НЕ ловит: его корень `недоступн` — от «недоступный».
  if (code === 'chat_unavailable') return tr('errors.chatUnavailable');
  // Тумблеры ИИ-заданий (docs/CONTRACTS/ai-skills.md): навык не включён, мок собрать
  // не из чего, окно мока не назначено, навык ещё не сдан в моке.
  // ⚠️ Без слова «куратор»: файл на пути новичка (newbieWords.test.ts), а фраза
  // «Куратор ещё не назначил» — в запретах сторожа сборки приложения (dist-mobile-guard.sh).
  if (code === 'trainers_closed') return tr('errors.trainersClosed');
  if (code === 'mock_no_skills') return tr('errors.mockNoSkills');
  if (code === 'mock_not_scheduled') return tr('errors.mockNotScheduled');
  if (code === 'mock_required') return tr('errors.mockRequired');
  // Повторная сдача уже сданной попытки — 409 (техдолг 121). Без своего кода 409 читался бы
  // общим «такое уже есть / данные изменились», а ученик решил бы, что работа пропала.
  if (code === 'mock_already_submitted') return tr('errors.mockAlreadySubmitted');
  if (code === 'sprint_already_submitted') return tr('errors.sprintAlreadySubmitted');
  // Лимиты и частота (промпт 12, 18.10.2026). Коды — с серверным шагом 2б; до него ниже разбирается текст.
  if (code === 'rate_limited') return tooFast(err);
  // ⚠️ У дневного лимита и расписания русский текст сервера точнее ключа: «тренажёр откроется 22.10», «на сегодня
  // разговор с экзаменатором уже был». С кодом он остаётся на ru, как было до кода; на kk/en — ключ (шаг 2б).
  // В моке (язык ответа en) `serverReason` даёт null — остаётся английский ключ.
  if (code === 'daily_limit' || code === 'daily_new_words') return serverReason(raw) ?? tr('errors.limitDay');
  if (code === 'weekly_limit') return weeklyLimit(err);
  if (code === 'out_of_schedule') return serverReason(raw) ?? tr('errors.outOfSchedule');
  // Спринт отвечает 409 pool_all_seen: без своего текста kk/en читали «данные изменились, обновите страницу», ru — сырой
  // текст сервера (шаг 2б, 20.10.2026). Тренажёр на том же коде (404) рисует свой экран «пул закончился».
  if (code === 'pool_all_seen') return tr('errors.poolAllSeen');
  // Чат: отказ сокета и HTTP (chat-service `hub.go`, `deny.go`).
  if (code === 'chat_inactive' || code === 'not_allowed' || code === 'not_participant') return tr('errors.chatClosed');
  if (code === 'save_failed') return tr('errors.chatNotSaved');

  if (isNetworkError(err)) return tr('errors.network');

  // 1) gin/validator: "...for 'Password' failed on the 'min' tag" (иногда с параметром)
  const gin = raw.match(/for '(\w+)'\s+failed on the '(\w+)' tag(?:.*?'?(\d+)'?)?/i);
  if (gin) return tagMessage(gin[1], gin[2], gin[3]);

  // Английские фразы служб без кода — свой текст, а не английский (сервер, ещё не перешедший на коды).
  if (bodyField(err, 'error') === 'rate limit exceeded' || /rate limit exceeded|too many (requests|messages)|slow down/i.test(raw)) {
    return tooFast(err);
  }
  if (/daily limit reached/i.test(raw)) return tr('errors.limitDay');
  if (/session is not active/i.test(raw)) return tr('errors.sessionEnded');

  // Лимит тренажёра (429) и расписание куратора (423). Сервер присылает
  // готовое человеческое сообщение — показываем его как есть: он знает, какой
  // именно лимит исчерпан и когда тренажёр откроется снова. На другом языке
  // интерфейса — свой текст по смыслу (неделя / день / расписание).
  //
  // ⚠️ 423 не заменять на 409: девятый код фронт трактует как «такое уже есть»
  // и подставляет свой текст про повтор — объяснение про расписание пропало бы.
  if (status === 429 || status === 423) {
    const shown = serverReason(raw);
    if (shown) return shown;
    if (status === 423) return tr('errors.outOfSchedule');
    if (HAS_CYRILLIC.test(raw)) {
      if (/недел/i.test(raw)) return tr('errors.limitWeek');
      if (/сегодня|в день|за день|сутки/i.test(raw)) return tr('errors.limitDay');
      return tr('errors.limitReached');
    }
  }

  // 409: причина сервера на языке ученика, иначе нейтральный текст. Раньше любой 409 становился «Такая запись уже
  // существует» — и «мест нет», и «комната закрыта», и «уже сдано» (промпт 12, 18.10.2026).
  if (status === 409) {
    const shown = serverReason(raw);
    if (shown) return shown;
  }

  // 2) частые подстроки
  if (low.includes('complexity') || low.includes('weak password')) {
    return tr('errors.weakPassword');
  }
  if ((low.includes('min') && (low.includes('length') || low.includes('character'))) || low.includes('too short')) {
    return tr('errors.shortValue');
  }
  if (low.includes('already exists') || low.includes('duplicate') || low.includes('unique')) {
    if (low.includes('email')) return tr('errors.emailExists');
    return tr('errors.recordExists');
  }
  if (low.includes('invalid credentials') || low.includes('invalid email or password')) {
    return tr('errors.badCredentials');
  }
  /**
   * ⚠️ БЛОКИРОВКА ПОСЛЕ ПЯТИ ОШИБОК ПОКАЗЫВАЛАСЬ ПО-АНГЛИЙСКИ. Сервер отвечает
   * «account is temporarily locked due to multiple failed attempts», а правила
   * для этой строки здесь не было — человек видел английский текст и не понимал
   * ни что случилось, ни что делать дальше.
   *
   * 07.09.2026, в день запуска, в эту стену упёрлись живые ученики: пятеро
   * подряд ошибались паролем и получали английскую фразу. Хуже того, из неё не
   * следует главное — что ждать надо не вечно, а четверть часа.
   */
  if (low.includes('temporarily locked') || low.includes('account is locked')) {
    return tr('errors.accountLocked');
  }
  /**
   * ⚠️ ПАРОЛЬ ВВЕД�ён ВЕРНО, НО С ПРОБЕЛОМ ИЛИ В ДРУГОМ РЕГИСТРЕ.
   *
   * Самая частая беда при входе: человек копирует пароль из письма вместе с
   * пробелом на конце — и получает «неверный пароль», хотя пароль у него
   * ПРАВИЛЬНЫЙ. Он пробует снова и снова тем же способом, упирается в
   * блокировку и уходит. Ровно это 07.09.2026 и происходило у живых учеников.
   *
   * Сервер отвечает так ТОЛЬКО тому, кто ввёл пароль целиком и верно, — то
   * есть уже его знает. Подбирающему такое не покажут.
   */
  if (low.includes('password almost right')) {
    return tr('errors.passwordAlmostRight');
  }
  if (low.includes('no sections')) {
    return tr('errors.emptyLesson');
  }
  if (low.includes('marathon has no modules') || (low.includes('marathon') && low.includes('no modules'))) {
    return tr('errors.marathonNoModules');
  }
  // Sequential-access gates return 403 with a specific reason — show it plainly
  // instead of the generic "Недостаточно прав", which confused users into
  // thinking access was mis-granted (it's the marathon's step-by-step mode).
  if (low.includes('complete the previous lesson')) {
    return tr('errors.previousLesson');
  }
  if (low.includes('homework must be approved') || (low.includes('previous lesson') && low.includes('approved'))) {
    return tr('errors.homeworkApproval');
  }
  if (low.includes('not enrolled in this marathon')) {
    return tr('errors.notEnrolled');
  }
  if (low.includes('invalid lesson status')) {
    return tr('errors.badLessonStatus');
  }
  if (low.includes('record not found') || low.includes('not found')) {
    return tr('errors.notFound');
  }
  if (low.includes('invalid character') || low.includes('cannot unmarshal') || low.includes('json')) {
    return tr('errors.checkData');
  }

  // 3) по коду статуса
  switch (status) {
    case 400:
      return serverReason(raw) ?? tr('errors.checkData');
    case 401:
      return tr('errors.badCredentials');
    case 403:
      // ⚠️ ПОКАЗЫВАЕМ ПРИЧИНУ, ЕСЛИ СЕРВЕР ЕЁ НАЗВАЛ. Общее «Недостаточно прав»
      // не говорит человеку ничего и выглядит как поломка: заказчик 05.09.2026
      // нажал «войти» в разговорную комнату и увидел именно его, хотя сервер
      // писал «эта комната не для вашей группы». То же правило уже действует
      // для 400: короткий человеческий текст показываем как есть.
      // Только на языке ученика (`serverReason`): английское «forbidden» больше не проходит.
      return serverReason(raw) ?? tr('errors.forbidden');
    case 404:
      return tr('errors.notFound');
    case 408:
    case 504:
      return tr('errors.timeout');
    case 409:
      return tr('errors.conflict');
    case 413:
      return tr('errors.fileTooBig');
    case 422:
      return tr('errors.checkData');
    case 429:
      return tr('errors.tooManyAttempts');
    default:
      if (status && status >= 500) return tr('errors.serverError');
  }

  // 4) запасной вариант.
  // ⚠️ НЕ `return raw` (25.09.2026): так на экран шли «Network Error» и английские тексты служб. Как есть —
  // только текст на языке интерфейса (кириллица: русский и казахский `new Error('…')` из кода и ответы служб)
  // и без внутренних слов; всё прочее — общий человеческий текст. Сторож — `errorTextGuard.test.ts`.
  // В моке (язык задан явно — английский) кириллица уже не «язык экрана».
  if (raw && HAS_CYRILLIC.test(raw) && !looksTechnical(raw) && !forcedLng?.startsWith('en')) return raw;
  return tr('errors.unknown');
}
